import SectionNotFoundException from '../exception/sectionNotFoundException'
import SettingNotFoundException from '../exception/settingNotFoundException'
import * as promises from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import { getElement } from '../utils'

const PAGE_TITLE = 'Notifications settings'

export async function getNotificationSection(label: string) {
  await getElement<HTMLElement>(document, `//*[text()="${PAGE_TITLE}"]`)

  const settingsPane = evaluateXpath<HTMLElement>(
    document,
    `//div[@role="main" and ${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'settings',
    )}]`,
  )[0]

  if (!settingsPane) {
    throw new Error(`Could not find settings pane for ${label}`)
  }

  const settingsRow = await getElement<HTMLElement>(
    settingsPane,
    `descendant::div[@aria-expanded and @role="button" 
    and .//*[text()="${label}"]]`,
  )

  if (!settingsRow) {
    throw new Error(`Could not find settings row for ${label}`)
  }

  return settingsRow
}

export async function getNotificationSectionSettingElement(
  sectionTitle: string,
  labelText: string,
  inputType: 'radio' | 'toggle',
): Promise<HTMLInputElement> {
  const notificationSection = await getNotificationSection(sectionTitle)
  if (!notificationSection) {
    throw new SectionNotFoundException(
      `Notification setting section not found for ${sectionTitle}`,
    )
  }

  if (notificationSection.ariaExpanded !== 'true') {
    notificationSection.click()
  }

  const settingsSection = evaluateXpath<HTMLElement>(
    notificationSection,
    `//div[@aria-labelledby="${notificationSection.id}" and @role="region"]`,
  )[0]

  if (!settingsSection) {
    throw new SectionNotFoundException(
      `Setting section not found for ${labelText} in ${sectionTitle}`,
    )
  }

  const settingRow = await getElement<HTMLElement>(
    settingsSection,
    `descendant::div[@role="button" and .//*[text()="${labelText}"]]`,
  )

  if (!settingRow) {
    throw new SectionNotFoundException(
      `Setting section not found for ${labelText} in ${sectionTitle}`,
    )
  }

  let settingInputSelector =
    'descendant::input[@role="switch" and @type="checkbox"]'
  if (inputType === 'radio') {
    settingInputSelector = 'descendant::input[@type="radio"]'
  }

  const settingInput = await getElement<HTMLInputElement>(
    settingRow,
    settingInputSelector,
  )

  if (!settingInput) {
    throw new SettingNotFoundException(
      `Setting ${inputType} not found for ${labelText} in ${sectionTitle}`,
    )
  }

  return settingInput
}

export async function clickRadioButton(
  sectionTitle: string,
  label: string,
): Promise<void> {
  const settingRadio = await getNotificationSectionSettingElement(
    sectionTitle,
    label,
    'radio',
  )

  const updatePromise = Promise.race([
    promises.getAttrPromise(settingRadio, 'aria-checked'),
    promises.getDomPromise(document, () => true),
    promises.throwIfTimeout(
      `clickRadioButton: Timed out; section ${sectionTitle}, label ${label} (1 minute)`,
      60000,
    ),
  ])

  settingRadio.click()
  await updatePromise
}

export async function getSelectedRadioText(
  sectionTitle: string,
  isMatchFunc: (element: HTMLElement) => boolean,
) {
  const notificationSection = await getNotificationSection(sectionTitle)

  const settingsSection = evaluateXpath<HTMLElement>(
    notificationSection,
    `//div[@aria-labelledby="${notificationSection.id}" and @role="region"]`,
  )[0]

  if (!settingsSection) {
    throw new SectionNotFoundException(
      `Setting section not found for radio buttons in ${sectionTitle}`,
    )
  }

  const selectedRadioButtons = evaluateXpath<HTMLElement>(
    settingsSection,
    'descendant::div[@role="button" and .//input[@type="radio" and @aria-checked="true"]]',
  )

  if (selectedRadioButtons.length === 0) {
    throw new SettingNotFoundException(
      `Could not find any selected radio buttons for notification setting in ${sectionTitle}`,
    )
  }

  const matchingRadioButton = selectedRadioButtons.find(isMatchFunc)

  if (!matchingRadioButton) {
    throw new SettingNotFoundException(
      `Could not find element for selected radio button matching criteria in ${sectionTitle}`,
    )
  }

  return matchingRadioButton.textContent
}

export async function getToggleOn(
  sectionTitle: string,
  label: string,
): Promise<boolean> {
  const settingToggle = await getNotificationSectionSettingElement(
    sectionTitle,
    label,
    'toggle',
  )

  return settingToggle.ariaChecked === 'true'
}

export async function setToggle(
  sectionTitle: string,
  label: string,
  targetValue: boolean,
): Promise<void> {
  const settingToggle = await getNotificationSectionSettingElement(
    sectionTitle,
    label,
    'toggle',
  )

  if (
    targetValue !== null &&
    targetValue !== undefined &&
    settingToggle.ariaChecked !== targetValue.toString()
  ) {
    const updatePromise = Promise.any([
      promises.getAttrPromise(settingToggle, 'aria-checked'),
      promises.getDomPromise(document, () => true),
    ])

    settingToggle.click()
    await updatePromise
  }
}
